import { Loader, ThemeProvider, TooltipProvider, ThemeContext, Theme } from '@afterpaytouch/core'
import { ThreatMatrix } from '@afterpaytouch/integrations/threat-matrix'
import { ErrorBoundary, Sentry, withSentryProfiler } from '../src/integrations/sentry'
import { getLaunchDarklyConfiguration, LaunchDarklyContext, withLDProvider } from '@afterpaytouch/feature'
import { Kasada } from '@afterpaytouch/integrations/kasada'
import { AppContextType, AppPropsType } from 'next/dist/shared/lib/utils'
import React, { ReactElement, useContext, useEffect } from 'react'
import { NextComponentType } from 'next'
import Head from 'next/head'
import type { AppInitialProps } from 'next/app'
import { appWithTranslation } from 'next-i18next'
import { Router } from 'next/router'
import { Container as ModalContainer } from 'react-modal-promise'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { Persistor } from 'redux-persist/es/types'
import { api, app, DEBUG_MODE } from '../src/env'
import { AmplitudeProvider } from '../src/integrations/amplitude'
import { initialise, profilingIdSelector, setFeatureFlagState, store, persistor, useAppDispatch, useLazyGetAccountQuery } from '../src/state'
import { MainLayout } from '../src/layouts/MainLayout'
import { Bouncer } from '../src/integrations/bouncer'
import { getLDContext } from '../src/utils/object'
import { AuthenticatedGlobalFetchProvider, NewPortalRolloutCookieProvider } from '../src/component'
import { useHasLoadedOnce } from '../src/hooks/useHasLoadedOnce'
import { ZenDesk } from '../src/integrations/zendesk'
import { useIsUnauthenticatedRoute } from '../src/config/router'
import { ThemeWrapper } from '../src/component/ThemeWrapper/ThemeWrapper'
import { useSetPostLoginCookies } from '../src/state/convergence/hooks'
import { getFaviconHref } from '../server/favicon'
import { PortalExperimentProvider } from '../src/providers/ExperimentProvider'
import './global.css'

const ldConfig = getLaunchDarklyConfiguration({
  clientSideID: app.LAUNCH_DARKLY_CLIENT_SIDE_ID,
  options: {
    // Sets sdkAppId so MAU can be grouped per app; these three share one LaunchDarkly environment.
    application: {
      id: 'afterpay-portal',
      version: app.APP_VERSION,
    },
    logger: {
      error: (message) => console.info(message),
      warn: (message) => console.info(message),
      info: () => {},
      debug: () => {},
    },
    sendEvents: !DEBUG_MODE,
  },
})

Sentry.init({
  dsn: app.SENTRY_DSN,
  environment: app.APP_ENVIRONMENT,
  version: app.APP_VERSION,
  ignoreHosts: ['api.amplitude.com', 'app.launchdarkly.com', 'events.launchdarkly.com'],
})

type Layout = (page: ReactElement) => ReactElement

type CustomAppComponent = NextComponentType & {
  getLayout?: Layout
}

interface CustomAppProps extends AppPropsType<Router, CustomPageProps> {
  Component: CustomAppComponent
  router: Router
}

interface CustomPageProps {
  theme: Theme
}

type ConsumerAppType = NextComponentType<AppContextType, AppInitialProps, CustomAppProps>

const ThemedFavicon = (): ReactElement => {
  const { theme } = useContext(ThemeContext)

  return (
    <Head>
      <link rel='icon' type='image/x-icon' href={getFaviconHref(theme.selected)} />
    </Head>
  )
}

const AppContent: React.FC<{ Component: CustomAppComponent; remainingPageProps: Record<string, unknown>; theme: Theme }> = ({
  Component,
  remainingPageProps,
  theme,
}) => {
  const { getLayout: ComponentLayout } = Component
  const getLayout = ComponentLayout ?? ((page) => <MainLayout>{page}</MainLayout>)
  const [triggerAccount, { isSuccess: isAccountSuccess, isUninitialized: isAccountUninitialized }] = useLazyGetAccountQuery()
  const dispatch = useAppDispatch()
  const isUnauthenticatedRoute = useIsUnauthenticatedRoute()
  const isAccountLoadSkipped = isUnauthenticatedRoute
  const hasAccountLookupLoadedOnce = useHasLoadedOnce(isAccountLoadSkipped || (isAccountSuccess && !isAccountUninitialized))

  const ldContext = getLDContext(
    store.getState(),
    () => {
      store.dispatch(setFeatureFlagState(false))
    },
    () => {
      store.dispatch(setFeatureFlagState(true))
    }
  )

  useSetPostLoginCookies()

  useEffect(() => {
    if (!isUnauthenticatedRoute) {
      triggerAccount()
    }
    dispatch(initialise())
  }, [isUnauthenticatedRoute])

  return (
    <>
      <ThemedFavicon />
      {hasAccountLookupLoadedOnce ? (
        <ThemeWrapper>
          <ErrorBoundary dsn={app.SENTRY_DSN} fallbackError={<></>}>
            <AmplitudeProvider>
              <LaunchDarklyContext {...ldContext}>
                <PortalExperimentProvider>
                  <TooltipProvider>
                    <NewPortalRolloutCookieProvider>
                      <AuthenticatedGlobalFetchProvider>
                        {getLayout(<Component {...remainingPageProps} />)}
                        <ModalContainer />
                      </AuthenticatedGlobalFetchProvider>
                    </NewPortalRolloutCookieProvider>
                  </TooltipProvider>
                </PortalExperimentProvider>
              </LaunchDarklyContext>
            </AmplitudeProvider>
          </ErrorBoundary>
        </ThemeWrapper>
      ) : (
        <div className='flex h-screen w-full items-center'>
          <Loader alignCenter />
        </div>
      )}
    </>
  )
}

const ConsumerApp: ConsumerAppType = ({ Component, pageProps }) => {
  const { theme, ...remainingPageProps } = pageProps

  return (
    <Provider store={store}>
      <Kasada src={app.KASADA_SRC} urlList={[api.PORTAL_API_BASE_URL, api.VERIFY_SERVICE_BASE_URL]} />
      <ThreatMatrix profilingSessionId={profilingIdSelector(store.getState())} tmOrganisationId={app.TM_ORGANISATION_ID} />
      <Bouncer />
      <ZenDesk />
      <ThemeProvider theme={theme}>
        <PersistGate persistor={persistor as Persistor} loading={<Loader alignCenter />}>
          <AppContent Component={Component} remainingPageProps={remainingPageProps} theme={theme} />
        </PersistGate>
      </ThemeProvider>
    </Provider>
  )
}

const withTranslation: ConsumerAppType = appWithTranslation(ConsumerApp)
const withLaunchDarkly = withLDProvider(ldConfig)(withTranslation)
const withSentry: ConsumerAppType = withSentryProfiler(withLaunchDarkly, { name: 'ConsumerPortalApp' })
export default withSentry
